"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const RULES = [
  {
    topic: "Report ID",
    rule: "Auto-generated with a QC- prefix unless you change prefix, next number, or auto-generate under the number preferences icon on Add Quality",
  },
  {
    topic: "Required fields",
    rule: "Type, QC Report Name, QC Report Responsible, Product Name, and at least one test",
  },
  {
    topic: "Report Type",
    rule: "One of four fixed values: In Material QC, On Production QC, Batch QC, R & D QC",
  },
  {
    topic: "Test response",
    rule: "Every test is either a preset multiple-choice scale, a set of custom measurement fields, or a single value — never free text",
  },
  {
    topic: "No active/inactive status",
    rule: "There's no deactivate action on a QC report, only Edit and Delete",
  },
  {
    topic: "Editing",
    rule: "Keeps a full history of every editor and when they saved, on every save",
  },
  {
    topic: "Deleting",
    rule: "Blocked if any item currently points to this report as its IQC, FQC, or OQC standard",
  },
  {
    topic: "Enforcement on transactions",
    rule: "None yet. No GRPO, Bill, Delivery Challan, or Production Order actually checks or blocks on a linked QC report",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. A QC report is a named checklist of tests" },
      { heading: "2. Each test picks a response scale, not free text" },
      { heading: "3. Where a QC report gets linked, and what actually happens" },
      { heading: "4. Editing and deleting" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p key={item.term} className="text-[16px] geist-regular text-[#374151] leading-relaxed">
        <span className="geist-medium text-[#111827]">{item.term}:</span> {item.desc}
      </p>
    ))}
  </div>
);

export default function QualityMasterPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Quality Master
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone building the QC checklists used to inspect materials, production runs,
            or finished goods
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A QC report here isn&apos;t just a name. It&apos;s a real{" "}
            <span className="geist-medium text-[#111827]">checklist of tests</span>,
            each with its own{" "}
            <span className="geist-medium text-[#111827]">response scale</span>,
            that you build once and can point an item&apos;s purchasing, sales,
            or production setup at later.
          </p>
        </motion.div>

        <hr className="my-8" />

        {/* What this module does */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          {/* 1. QC report */}
          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. A QC report is a named checklist of tests
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Four fields identify the report itself, before you get to what it actually checks.
          </p>
          <Definitions
            items={[
              {
                term: "QC Report Name and Product Name",
                desc: "What the report is called and what it applies to.",
              },
              {
                term: "QC Report Responsible",
                desc: "Who's named as the checker on this report.",
              },
              {
                term: "Type",
                desc: "One of four fixed categories: In Material QC, On Production QC, Batch QC, or R & D QC.",
              },
              {
                term: "Report ID",
                desc: "Assigned automatically with a QC- prefix unless you change prefix or next number on the number preferences icon. You cannot type a one-off ID on the form.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Add Quality form with Type, QC Report Name, and Product Name fields." />

          {/* 2. Tests */}
          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Each test picks a response scale, not free text
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            <span className="geist-medium text-[#111827]">Add Test Type</span>{" "}
            doesn&apos;t ask you to build a response scale from scratch. You
            pick one of six presets, and it fills in the choices for you.
          </p>
          <Definitions
            items={[
              {
                term: "Yes / No, Pass / Fail, Compliant / Non-Compliant, Good / Fair / Poor",
                desc: "Four ready-made multiple-choice scales, each with its own N/A option.",
              },
              {
                term: "Our Standard",
                desc: "Switches the test to custom numeric fields instead: weight, length, width, height, and volume.",
              },
              {
                term: "Size",
                desc: "A single value response rather than a multiple-choice one.",
              },
              {
                term: "Title on QC Report and Remark",
                desc: "Every test also carries its own display title and an optional remark, independent of which scale you picked.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Add Test Details modal with the Pass / Fail / N-A preset selected." />

          {/* 3. Linking */}
          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Where a QC report gets linked, and what actually happens
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            An item can point at a{" "}
            <span className="geist-medium text-[#111827]">QC report</span> from
            three of its own tabs.
          </p>
          <Definitions
            items={[
              {
                term: "Purchasing Data",
                desc: "Before Dispatch From Vendor Factory and After GRPO On Our Warehouse can each carry their own IQC standard.",
              },
              {
                term: "Sales Data",
                desc: "Before Dispatch From Warehouse carries an FQC standard.",
              },
              {
                term: "Production Data",
                desc: "OQC carries its own standard for output leaving production.",
              },
            ]}
          />
          <Callout type="warning">
            Linking a report to an item saves the reference and nothing more.
            No GRPO, Bill, Delivery Challan, or Production Order currently
            checks or blocks on it. Treat this as a checklist definition, not
            an enforced gate, until that changes.
          </Callout>
          <Screenshot caption="Screenshot: the IQC Standard field on an item's Purchasing Data tab, pointing at a QC report." />

          {/* 4. Editing/Deleting */}
          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Editing and deleting
          </h3>
          <Definitions
            items={[
              {
                term: "Editing",
                desc: "Every save adds to a running history of who edited the report and when, rather than overwriting who last touched it.",
              },
              {
                term: "Deleting",
                desc: "Blocked outright if any item still names this report as its IQC, FQC, or OQC standard. There's no deactivate step in between. A report is either in use or deletable.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Actions menu on a QC report's detail page, showing Edit and Delete." />
        </motion.div>

        <hr className="my-8" />

        {/* Business rules */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr key={r.topic} className={i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""}>
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">{r.rule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Related */}
        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
          <Link
            href="/documentation/books/all-master/item-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Item Master <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Where IQC, FQC, and OQC standards actually get linked to a product
            </p>
          </Link>
          <Link
            href="/documentation/books/all-master/recipe"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Recipe <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              The Process Route an OQC-linked item is produced through
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
